/* eslint-disable @typescript-eslint/naming-convention */
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { DTypeForm } from '@grupo-constant/next-dtype/form';
import { DTypeRepository, DTypeStatus, NextLocator } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { format } from 'date-fns';
import { cloneDeep } from 'lodash-es';
import { catchError, firstValueFrom, map, Subject, takeUntil, throwError } from 'rxjs';
import { MasterDataService } from '../master-data.service';
import { DownloadMasterXSSFBody, MasterData } from '../master-data.types';
import { UserNodePrivileges } from '../../../modules/admin/projects/projects.types';
import { DTypeAlerts, ToggleDTypeHiddenBody, ToggleDTypeLockBody } from '../../dtype/dtype.types';
import { DialogsService } from '../../../dialogs/dialogs.service';
import { DTypeService } from '../../dtype/dtype.service';
import { LocalizePipe } from '../../pipes/localize.pipe';
import { LocalizeService } from '../../pipes/localize.service';
import { ProjectsService } from '../../../modules/admin/projects/projects.service';
import { UserService } from '../../user/user.service';
import { ConfirmationWithCountdownConfig } from '../../../dialogs/confirm-with-countdown-dialog/confirm-with-countdown-dialog.types';

@Component({
    selector: 'master-summary',
    templateUrl: './master-summary.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MasterSummaryComponent implements OnInit, OnDestroy {

    masterData: MasterData;

    params: Params;
    uri: string;
    locator: NextLocator;
    repo: DTypeRepository;
    role: UserNodePrivileges;
    sameLocator: boolean;

    showAlerts: DTypeAlerts = {
        showScheduleToGoLive: true,
        showMigrating: true,
        showHasRequestToGoLive: true,
    };

    scheduleAlert: string;

    databaseViewMode: boolean;
    quarantineViewMode: boolean;

    dtypeForm: DTypeForm;
    loading = true;

    private _unsubscribeAll: Subject<void> = new Subject<void>();

    constructor(
        private activatedRoute: ActivatedRoute,
        private _changeDetectorRef: ChangeDetectorRef,
        private dialogsService: DialogsService,
        private dtypeService: DTypeService,
        private localize: LocalizePipe,
        private localizeService: LocalizeService,
        private masterDataService: MasterDataService,
        private projectsService: ProjectsService,
        private router: Router,
        private _snackbar: MatSnackBar,
        private translocoService: TranslocoService,
        private userService: UserService
    ) { }

    ngOnInit() {
        this.activatedRoute.queryParams
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(async (params) => {
                this.loading = true;

                this.databaseViewMode = params.databaseViewMode ?? false;
                this.params = params;

                if (params.uri && params.locator) {
                    this.uri = params.uri;
                    this.locator = this.masterDataService.getLocatorFromString(this.uri.substring(9, this.uri.lastIndexOf('/'))); //master://
                    this.role = await this.projectsService.getRoleByNextLocators(this.locator);
                    this.repo = cloneDeep(await firstValueFrom(this.masterDataService.retrieveMasterDataDTypeRepositoryByUri(this.uri, DTypeStatus.LIVE, this.locator)));
                    this.masterData = this.repo.payload as MasterData;
                    this.sameLocator = this.projectsService.isTheSameLocator(params.locator, this.repo.belongsTo);
                } else {
                    this.dialogsService.openErrorDialog();
                    this.router.navigate(['/projects'], { queryParams: { force: true } });
                    return;
                }

                this.dtypeForm = await this.masterDataService.retrieveMasterDataDtypeForm(this.masterData, { readonly: true, isUpdate: true });
                if (this.masterData.pseudoStatic) {
                    for (const key of Object.keys(this.dtypeForm['fields'])) {
                        if (key !== 'uri' && key !== 'name' && key !== 'caption') {
                            this.dtypeForm['fields'][key].hidden = true;
                        }
                    }
                }

                if (params?.dataVerifier && this.role?.dataVerifier && this.repo.locked && this.repo.dtypeToGoLiveRequest && !this.repo.dtypeToGoLiveSchedule) {
                    this.quarantineViewMode = true;
                }

                if (this.repo.dtypeToGoLiveSchedule && !this.repo.migrating) {
                    this.calculateScheduleAlert();
                }

                this._changeDetectorRef.detectChanges();
                this.loading = false;
            });
    }

    ngOnDestroy() {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    deleteRepository() {
        this.dtypeService.deleteNextDType(this.uri).subscribe({
            next: (reply) => {
                if (reply?.status === 200) {
                    this._snackbar.open(this.translocoService.translate('deletedDType',
                        { name: this.localize.transform(this.masterData?.caption) }), this.translocoService.translate('accept'), { duration: 5000 });
                    this.goBack();
                } else {
                    this.dialogsService.openErrorDialog();
                }
            }, error: (err) => {
                console.error(err);
                this.dialogsService.openErrorDialog({
                    message: err?.error?.echo ? this.localize.transform(err?.error?.echo) : undefined,
                });
            }
        });
    }

    changeView() {
        this.databaseViewMode = !this.databaseViewMode;
    }

    async changeQuarantineView(reload: boolean = false) {
        if (reload) {
            this.loading = true;
            this.masterDataService.retrieveMasterDataDTypeRepositoryByUri(this.uri, DTypeStatus.LIVE, this.locator).pipe(
                catchError((error) => {
                    this.dialogsService.openErrorDialog({
                        message: error?.error?.echo ? this.localize.transform(error?.error?.echo) : undefined,
                    });
                    console.error(error);
                    return throwError(() => error.error);
                })).subscribe(async (repo) => {
                this.repo = repo;
                this.masterData = repo.payload as MasterData;
                if (this.repo.dtypeToGoLiveSchedule && !this.repo.migrating) {
                    this.calculateScheduleAlert();
                }
                this.loading = false;
                this.quarantineViewMode = !this.quarantineViewMode;
            });
        } else {
            this.quarantineViewMode = !this.quarantineViewMode;
        }
    }

    hasPermissionToDesignRepository() {
        return this.projectsService.hasPermissionToDesignRepository(this.role, 'master');
    }

    async downloadStructure() {
        const date = (format(new Date(), 'yyyy-MM-dd_HH-mm-ss'));
        const filenameFront = this.localize.transform(this.masterData?.name) + '_r_' + this.repo.release + '_d_' + date;
        const body: DownloadMasterXSSFBody = { uri: this.uri, language: this.translocoService.getActiveLang() };
        this.masterDataService.downloadStructureXSSF(body).pipe(
            catchError((error) => {
                console.error(error);
                let _message = null;
                if (error?.status === 404) {
                    _message = 'No se ha encontrado el repositorio.';
                }
                this.dialogsService.openErrorDialog({
                    message: _message ? _message : undefined,
                });
                return throwError(() => error);
        })).subscribe((data) => {
            const filename = String(data?.headers?.get('content-disposition')).replace('attachment; filename="', '').replace('"', '');
            const element = document.createElement('a');
            element.href = window.URL.createObjectURL(data.body);
            element.download = filename ?? filenameFront;
            element.click();
        });
    }

    async toggleDTypeRepositoryLock() {
        this.loading = true;
        const config: ConfirmationWithCountdownConfig = {
            title: this.translocoService.translate(this.repo.locked ? 'blockRepoDialog.titleLocked' : 'blockRepoDialog.titleUnlocked'),
            message: this.translocoService.translate(this.repo.locked ? 'blockRepoDialog.messageLocked' : 'blockRepoDialog.messageUnlocked'),
            countdownMessage: this.translocoService.translate('blockRepoDialog.countdownMessage'),
            seconds: 10,
            actions: {
                confirm: {
                    show: true,
                    label: this.translocoService.translate(this.repo.locked ? 'blockRepoDialog.unlock' : 'blockRepoDialog.lock'),
                    color: 'warn'
                },
                cancel: {
                    show: true,
                    label: this.translocoService.translate('cancel'),
                }
            }
        };

        const confirmed = await firstValueFrom(this.dialogsService.openConfirmWithCountdownDialog(config).afterClosed());

        if (confirmed === 'confirmed') {
            const body: ToggleDTypeLockBody = {
                uri: this.repo.uri,
            };

            this.dtypeService.toggleDTypeLock(body).pipe(
                catchError((error) => {
                    this.dialogsService.openErrorDialog({
                        message: error?.error?.echo ? this.localize.transform(error?.error?.echo) : undefined,
                    });
                    console.error(error);
                    return throwError(() => error.error);
                }),
                map(reply => reply.repository)
            ).subscribe(async (repo) => {
                this.repo = repo;
                this.masterData = repo.payload as MasterData;
                this.loading = false;
                this._snackbar.open(this.translocoService.translate(
                    this.repo.locked ? 'blockRepoDialog.lockedRepo' : 'blockRepoDialog.unlockedRepo'), this.translocoService.translate('accept'), { duration: 5000 });
            });
        }
        this.loading = false;
        //TODO AD: falta la parte de mostrar dependencias que está pendiente de servidor
    }

    async toggleDTypeRepositoryHidden() {
        this.loading = true;
        const config: ConfirmationWithCountdownConfig = {
            title: this.translocoService.translate(this.repo.hidden ? 'hideRepoDialog.show' : 'hideRepoDialog.hide'),
            message: this.translocoService.translate(this.repo.hidden ? 'hideRepoDialog.messageShow' : 'hideRepoDialog.messageHide'),
            countdownMessage: this.translocoService.translate('hideRepoDialog.countdownMessage'),
            seconds: 10,
            actions: {
                confirm: {
                    show: true,
                    label: this.translocoService.translate(this.repo.hidden ? 'hideRepoDialog.show' : 'hideRepoDialog.hide'),
                    color: 'warn'
                },
                cancel: {
                    show: true,
                    label: this.translocoService.translate('cancel'),
                }
            }
        };

        const confirmed = await firstValueFrom(this.dialogsService.openConfirmWithCountdownDialog(config).afterClosed());

        if (confirmed === 'confirmed') {
            const body: ToggleDTypeHiddenBody = {
                uri: this.repo.uri,
            };

            this.dtypeService.toggleDTypeHidden(body).pipe(
                catchError((error) => {
                    this.dialogsService.openErrorDialog({
                        message: error?.error?.echo ? this.localize.transform(error?.error?.echo) : undefined,
                    });
                    console.error(error);
                    return throwError(() => error.error);
                }),
                map(reply => reply.repository)
            ).subscribe(async (repo) => {
                this.repo = repo;
                this.masterData = repo.payload as MasterData;
                this.loading = false;
                this._snackbar.open(this.translocoService.translate(
                    this.repo.hidden ? 'hideRepoDialog.hiddenRepo' : 'hideRepoDialog.showedRepo'), this.translocoService.translate('accept'), { duration: 5000 });
            });
        }
        this.loading = false;
    }

    async cancelQuarantine() {
        const confirm2 = await this.openConfirmDialogWithCountdown(
            this.translocoService.translate('quarantineReject.title'),
            this.translocoService.translate('quarantineReject.message'),
            this.translocoService.translate('quarantineReject.countdownMessage'));
        if (confirm2) {
            this.dtypeService.flushDTypeToGoLiveSchedule({uri: this.uri, rejected: true}).pipe(
                catchError((error) => {
                    this.dialogsService.openErrorDialog({
                        message: error?.error?.echo ? this.localize.transform(error?.error?.echo) : undefined,
                    });
                    console.error(error);
                    return throwError(() => error.error);
                }),
                map(reply => reply.repository)
            ).subscribe(async (repo) => {
                this.repo = repo;
                this.masterData = repo.payload as MasterData;
            });
        }
    }

    private goBack() {
        this.router.navigate(['../'], {
            queryParams: {
                ou: '01',
                marca_code: this.locator.marca_code,
                cliente_code: this.locator.cliente_code,
                projectId: this.locator.projectId,
                serviceId: this.locator.serviceId
            },
            relativeTo: this.activatedRoute
        });
    }

    private calculateScheduleAlert() {
        this.scheduleAlert = this.translocoService.translate('quarantineScheduled.part1') +
                    this.localizeService.transformDate(this.repo.dtypeToGoLiveSchedule.date, 'dd/MM/yyyy H:mm') +
                    (this.role?.dataVerifier ? this.translocoService.translate('quarantineScheduled.part2') : this.translocoService.translate('quarantineScheduled.part3'));
    }

    private openConfirmDialogWithCountdown(title: string, message: string, countdownMessage: string) {
        const dialog = this.dialogsService.openConfirmWithCountdownDialog({
            title,
            message,
            countdownMessage,
            seconds: 10,
            actions: {
                confirm: {
                    label: this.translocoService.translate('confirm')
                },
                cancel: {
                    label: this.translocoService.translate('revert'),
                }
            },
        });

        return new Promise<boolean>((resolve) => {
            dialog.afterClosed().subscribe((result) => {
                if (result === 'confirmed') {
                    resolve(true);
                } else {
                    resolve(false);
                }
            });
        });
    }
}
